import { useBrainStore } from './brainStore';
import { PERSISTABLE_ENTITIES } from './persistableEntities';
import { scheduleWrite, flushWrites } from '@/state/writeQueue';
import { queryClient } from '@/api/queryClient';
import { applyWriteToCache, invalidateWriteKeys, invalidateWriteViews, patchWriteIntoCache } from '@/state/reactivityContract';
import { useUIStore } from '@/state/uiStore';

/**
 * Write-through bridge: subscribes to the brain store and, on every change,
 * diffs each persistable collection against the previous state — PUTting new /
 * changed records and DELETEing removed ones. Generic, so no mutation needs to
 * know about persistence, and a new collection just joins PERSISTABLE_ENTITIES.
 *
 * MUST be installed AFTER initial hydration + journey replay, so the baseline
 * is the loaded world and only genuine user mutations persist (no load-storm).
 * Records are compared by reference: an unchanged record is the same object, so
 * only the ids a mutation actually replaced get written.
 */
type Collection = Record<string, unknown>;
let unsubscribe: (() => void) | null = null;

/**
 * `${entity}:${id}` keys currently being reverted after a server rejection
 * (409 booking-conflict from tv-1vh.11, or a 422 domain-validation from
 * tv-ods.1). The revert is itself a `useBrainStore.setState` call, and zustand
 * notifies subscribers synchronously/inline (not on a microtask) — so that
 * setState re-enters THIS module's subscriber before `handleWriteReject`
 * returns. Without this guard, that re-entrant pass would see the id's value
 * change (erroneous optimistic value -> reverted value) and treat it like a
 * fresh user mutation: PUT the reverted value straight back (a create-revert
 * would even look like a delete to persist). Populated immediately before the
 * revert `setState` and cleared immediately after, so it only ever suppresses
 * that one re-entrant pass.
 */
const revertingIds = new Set<string>();

/**
 * `${entity}:${id}` keys whose current value was authored by a SERVER intent
 * (e.g. POST /api/intents/pay-deposit — tv-rp7.1), not by a plain client
 * mutation. The server transaction has ALREADY persisted these records, so the
 * bridge must NOT schedule a second PUT (that would be a redundant — and
 * possibly racing — double write). It MUST still `applyWriteToCache`, so the
 * mounted view reflects the flip optimistically (ghost→solid) with no /ws frame
 * and no refetch. This is the SUBTLE difference from `revertingIds`, which
 * suppresses BOTH the write and the cache echo.
 *
 * Populated by `withServerAuthoredWrites` immediately before the brain-store
 * mutation and cleared immediately after — the same inline-notify timing
 * reasoning as `revertingIds`: zustand notifies this subscriber synchronously,
 * so the ids must be present for exactly that re-entrant pass and gone after.
 */
const serverAuthoredIds = new Set<string>();

/**
 * Run a synchronous brain-store mutation whose records a server intent already
 * persisted: mark the `[entity, id]` pairs server-authored (add BEFORE the
 * setState, remove AFTER), so the bridge's subscriber SKIPS the PUT for them but
 * still patches the query cache. Everything else in `fn` (and every other id)
 * persists normally.
 */
export function withServerAuthoredWrites(
  pairs: ReadonlyArray<readonly [string, string]>,
  fn: () => void,
): void {
  const keys = pairs.map(([entity, id]) => `${entity}:${id}`);
  for (const key of keys) serverAuthoredIds.add(key);
  try {
    fn();
  } finally {
    for (const key of keys) serverAuthoredIds.delete(key);
  }
}

/**
 * True while a WHOLE-STORE update that did NOT originate from a local edit is
 * being applied — a cross-tab BroadcastChannel merge or a re-hydrate. Such an
 * apply replaces every collection reference, so the reference diff below would
 * treat every record as "changed" and re-PUT (or, for an emptied collection,
 * DELETE) the entire store over the server's authoritative rows — the tv-k9b.14
 * clobber (a stale `pending` deposit re-PUT over `paid` → 422 storm; bookings
 * reverted/deleted → vanish). The bridge skips its whole pass while this is set.
 */
let applyingRemoteState = false;

/**
 * Apply a remote-authored / re-hydrated whole-store update WITHOUT the bridge
 * re-persisting it. The server already owns these rows (they arrived from another
 * tab or from /api/bootstrap), so mirroring them into the store must not schedule
 * a single PUT/DELETE. Nesting-safe (restores the prior flag), and it clears even
 * if `fn` throws.
 */
export function withoutWriteThrough(fn: () => void): void {
  const prev = applyingRemoteState;
  applyingRemoteState = true;
  try {
    fn();
  } finally {
    applyingRemoteState = prev;
  }
}

/**
 * Reverts an optimistic write the server refused (409 or 422): restores `was`
 * (or deletes the row if this was a create) in the entity's collection, discards
 * the optimistic query-cache patch so views refetch the server's true state, and
 * surfaces a critical toast. Owned here (not persist.ts) because the diff loop
 * below is what has `was` in scope. Generic over entity (tv-ods.1) while
 * preserving the exact booking-conflict UX from tv-1vh.11.
 */
function handleWriteReject(
  entity: string,
  id: string,
  was: unknown,
  status: number,
  body: { message?: string } | undefined,
): void {
  const key = `${entity}:${id}`;
  revertingIds.add(key);
  try {
    useBrainStore.setState((s) => {
      const coll = { ...((s as unknown as Record<string, Record<string, unknown>>)[entity] ?? {}) };
      if (was === undefined) {
        delete coll[id];
      } else {
        coll[id] = was;
      }
      return { [entity]: coll } as never;
    });
  } finally {
    revertingIds.delete(key);
  }

  // Discard the optimistic view patch so the affected views refetch server truth.
  // Scoped to exactly the keys this entity feeds via the shared reactivity
  // contract (tv-jp2) — the same entity→keys mapping applyWriteToCache uses, and
  // never the bare all-queries invalidate. `was` carries the studioId (when
  // known) so the studio-scoped calendar/availability keys stay per-studio.
  invalidateWriteKeys(queryClient, entity, was);

  // Preserve the exact tv-1vh.11 booking-conflict toast; otherwise surface the
  // server's rejection reason.
  if (entity === 'bookings' && status === 409) {
    useUIStore.getState().pushNotification({
      intensity: 'critical',
      title: 'Booking conflict',
      body: 'That artist already has a firm booking overlapping this time.',
      ttlMs: 10000,
    });
  } else {
    useUIStore.getState().pushNotification({
      intensity: 'critical',
      title: 'Change rejected',
      body: body?.message ?? 'The server rejected this change, so it was undone.',
      ttlMs: 10000,
    });
  }
}

/**
 * The artist self-book placeholder customer (selfBookFlow AWAITING_CLIENT —
 * kept literal here so the bridge takes no domain import). It is not a customer
 * row, so a record carrying it can never land server-side (customer FK).
 */
const AWAITING_CLIENT_ID = 'awaiting-client';

/**
 * tv-2na.9.11 (D26): a self-book pin's held deposit + booking for the
 * AWAITING_CLIENT placeholder are LOCAL-ONLY. Their PUTs failed the customer FK
 * (a 500 the write queue retried forever — and kept re-PUTting after the client
 * paid, over the server-authored rows). The server holds the pin on the
 * self-book link itself and mints the real rows at pay, so these are never PUT
 * (the view cache is still patched, so the artist sees their hold).
 */
function isLocalOnlyPlaceholder(entity: string, record: unknown): boolean {
  if (entity !== 'deposits' && entity !== 'bookings') return false;
  return (record as { customerId?: unknown } | undefined)?.customerId === AWAITING_CLIENT_ID;
}

/** Idempotent: a second call returns the existing disposer. */
export function installPersistenceBridge(): () => void {
  if (unsubscribe) return unsubscribe;

  const off = useBrainStore.subscribe((state, prev) => {
    // A remote / re-hydrated whole-store apply is server-authoritative — never
    // re-PUT or DELETE it back (tv-k9b.14). Skip the entire pass; the next real
    // local edit diffs against this applied state as its baseline.
    if (applyingRemoteState) return;

    const cur = state as unknown as Record<string, Collection | undefined>;
    const old = prev as unknown as Record<string, Collection | undefined>;

    for (const entity of PERSISTABLE_ENTITIES) {
      const now = cur[entity];
      const was = old[entity];
      // Whole-collection reference unchanged → nothing in it moved.
      if (!now || now === was) continue;

      for (const id in now) {
        // Skip the bridge's own in-flight revert — see revertingIds.
        if (revertingIds.has(`${entity}:${id}`)) continue;
        if (!was || now[id] !== was[id]) {
          const prior = was ? was[id] : undefined;
          // A server-authored record was already persisted by the intent — skip
          // the PUT (no double write) but STILL echo it to the cache below.
          const record = now[id];
          if (!serverAuthoredIds.has(`${entity}:${id}`) && !isLocalOnlyPlaceholder(entity, record)) {
            // Optimistically reflect the own write in the query cache (tv-lop) at
            // once; refetch the views it feeds only once the PUT has LANDED (B2),
            // so they never read the old server row back over it.
            patchWriteIntoCache(queryClient, entity, id, record);
            scheduleWrite(
              entity,
              id,
              'put',
              record,
              (status, body) => handleWriteReject(entity, id, prior, status, body),
              () => invalidateWriteViews(queryClient, entity, record),
            );
          } else {
            // Already on the server (or never sent): patch and refetch now.
            applyWriteToCache(queryClient, entity, id, record);
          }
        }
      }
      if (was) {
        for (const id in was) {
          if (revertingIds.has(`${entity}:${id}`)) continue;
          if (!(id in now)) {
            // Same server-authored suppression as the put branch: skip the
            // DELETE the server already applied, still echo it to the cache.
            if (!serverAuthoredIds.has(`${entity}:${id}`)) {
              patchWriteIntoCache(queryClient, entity, id, undefined);
              scheduleWrite(entity, id, 'delete', undefined, undefined, () =>
                invalidateWriteViews(queryClient, entity, undefined),
              );
            } else {
              applyWriteToCache(queryClient, entity, id, undefined);
            }
          }
        }
      }
    }
  });

  // Flush any pending coalesced writes when the tab is closing or backgrounded,
  // using keepalive so the browser doesn't cancel them during teardown.
  const flushOnExit = () => flushWrites({ keepalive: true });
  const onVisibility = () => { if (document.visibilityState === 'hidden') flushOnExit(); };
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', flushOnExit);
    document.addEventListener('visibilitychange', onVisibility);
  }

  unsubscribe = () => {
    off();
    if (typeof window !== 'undefined') {
      window.removeEventListener('beforeunload', flushOnExit);
      document.removeEventListener('visibilitychange', onVisibility);
    }
    flushWrites(); // don't strand pending writes when the bridge is torn down
    unsubscribe = null;
  };
  return unsubscribe;
}
